/* eslint-disable react/jsx-props-no-spreading */
import React, { useContext, useMemo } from 'react';
import GoogleMapReact from 'google-map-react';
import lodashDebounce from 'lodash/debounce';

import exportWithBoundary from '../../../../helpers/exportWithBoundary';
import { JobMapContext } from '../JobMapContext';
import JobMapMarker from '../JobMapMarker';
import useMarkers from '../useMarkers';
import { MapComponent as Map } from './styles';

function JobMap() {
  const {
    allJobsBounds,
    jobMapId,
    mapRef,
    params,
    setParams,
    setCurrentJob,
    setCurrentPlace,
    setZoomOutToJobs,
    zoomOutToJobs,
    isMapCenterConfigured,
  } = useContext(JobMapContext);

  const { data: markerData, refetch: refetchMarkers } = useMarkers(
    jobMapId,
    params,
    {
      onSuccess: ({ entries, meta }) => {
        if (entries && entries.length > 0 && !allJobsBounds.current) {
          const bounds = new window.google.maps.LatLngBounds();

          entries.map((x) => {
            bounds.extend({ lat: x.bounds.north, lng: x.bounds.east });
            bounds.extend({ lat: x.bounds.south, lng: x.bounds.west });
            return x;
          });

          allJobsBounds.current = bounds.toJSON();

          if (!isMapCenterConfigured) {
            mapRef.current.fitBounds(bounds);
          }

          return;
        }

        if (zoomOutToJobs === true && meta.total_count === 0) {
          mapRef.current.setZoom(params.zoom - 1);

          return;
        }

        setZoomOutToJobs(false);
      },
      enabled: !!mapRef.current,
    }
  );

  const jobMarker = (marker) => {
    const handleClick = () => {
      setCurrentJob(null);
      if (marker.locations_count === 1) {
        setCurrentPlace(marker);
      } else if (mapRef.current) {
        mapRef.current.fitBounds(marker.bounds);
      }
    };
    return (
      <JobMapMarker
        key={marker.geohash}
        marker={marker}
        onClick={handleClick}
        lat={marker.lat}
        lng={marker.lng}
      />
    );
  };

  const onBoundsChanged = (map) => {
    const bounds = map?.getBounds();
    if (bounds && allJobsBounds.current) {
      setParams((prev) => ({
        ...prev,
        zoom: map.getZoom(),
        bounds: bounds.toJSON(),
      }));
    } else {
      refetchMarkers();
    }
  };

  const debouncedOnBoundsChanged = useMemo(
    () => lodashDebounce(onBoundsChanged, 1000),
    []
  );

  const bindBoundsListener = (map, maps) => {
    maps.event.addDomListenerOnce(map, 'idle', () => {
      maps.event.addListener(map, 'bounds_changed', () => {
        debouncedOnBoundsChanged(map);
      });
      debouncedOnBoundsChanged(map);
    });
  };

  // Fit map to its bounds after the api is loaded
  const apiIsLoaded = (map, maps) => {
    if (map) {
      mapRef.current = map;
      map.setOptions({
        clickableIcons: false,
        minZoom: 1,
      });
      // Bind the bounds listener
      bindBoundsListener(map, maps);
    }
  };

  return (
    <Map>
      <GoogleMapReact
        bootstrapURLKeys={{
          key: 'AIzaSyBxWPGy-pjLdSkOBjEIcgkwLuzivzfTDd4',
        }}
        center={params.center}
        zoom={params.zoom}
        yesIWantToUseGoogleMapApiInternals
        onGoogleApiLoaded={({ map, maps }) => apiIsLoaded(map, maps)}
      >
        {markerData?.entries?.map((x) => jobMarker(x))}
      </GoogleMapReact>
    </Map>
  );
}

function JobMapFallback() {
  return <Map />;
}

export default exportWithBoundary(JobMap, JobMapFallback);
